import SaveIcon from '@mui/icons-material/Save';
import { Button, Checkbox, styled, Typography } from '@mui/material';
import { StopType } from 'harmony-constants';
import { L } from 'harmony-language';
import React from 'react';
import { OrgQueryKeys } from '../../../../api/config';
import { useMergeDraftsMutation } from '../../../../api/mutations/reworked/use-merge-drafts-mutation';
import { useOrganizationQuery } from '../../../../api/queries/use-organization-query';
import { AgisticsColors } from '../../../../styles/agistics-colors';
import { Load, OrganizationLocation, Trailer } from '../../../../types';
import { createAgisticsSelectElements } from '../../../shared/agistics-select';
import { useWeights } from '../../../shared/hooks/use-weights';
import LoadingButton from '../../../shared/loading-button';
import { AgisticsMultiSelect } from '../../../shared/multi-select/agistics-multi-select';
import { LoadTableStops } from '../../table-components/stops/load-table-stops';
import { mergeDrafts } from './merge-utils';

interface AutoMergeDraftsProps {
    drafts: Load[];
    onSubmit: (ids: number[]) => void;
}

export const AutoMergeDrafts = (props: AutoMergeDraftsProps) => {
    const { drafts, onSubmit } = props;
    const { convertFromGramsDisplay } = useWeights()
    const { data: throughputs } = useOrganizationQuery(OrgQueryKeys.throughput);
    const { data: organizationLocations = [] } = useOrganizationQuery<OrganizationLocation[]>(OrgQueryKeys.locations);
    const { data: trailers = [] } = useOrganizationQuery<Trailer[]>(OrgQueryKeys.trailers);
    const { mutate: submitMergeDrafts, isLoading: isMergingDraft } = useMergeDraftsMutation();

    const [ autoMergeAttempted, setAutoMergeAttempted ] = React.useState(false);
    const [ selectedWeightCapacities, setSelectedWeightCapacities ] = React.useState<number[]>([]);
    const [ mergeResults, setMergeResults ] = React.useState<Load[]>([]);
    const [ selectedResultIndexes, setSelectedResultIndexes ] = React.useState<number[]>([]);
    const [ isSubmitting, setIsSubmitting ] = React.useState(false);

    const cargoWeightObjects = Array.from(new Set(trailers.map(trailer => trailer.maxCargoWeight).filter(Boolean)))
        .map(weight => ({
            id: weight,
            value: convertFromGramsDisplay(weight)
        }));

    const promptLabel = !autoMergeAttempted
        ? `${L.autoMergeSelectCapacity()}`
        : mergeResults.length > 0
            ? `${L.autoMergeSelectDrafts()}`
            : `${L.autoMergeNoDraftsFound()}`;

    const runAutoMerge = () => {
        const mergeResults: Load[] = [];
        // Right now, only single destination drafts with a weight are eligible for auto-merge
        const draftPool = drafts
            .filter(draft => draft.stops.filter(stop => stop.type === StopType.Destination && stop.weight).length === 1)
            .sort((a, b) => b.stops.find(stop => stop.type === StopType.Destination)!.weight! - a.stops.find(stop => stop.type === StopType.Destination)!.weight!);
        // Fill weight capacities from largest to smallest until eligible drafts are exhausted
        selectedWeightCapacities.sort((a, b) => b - a).forEach(weightCapacity => {
            while (draftPool.length > 1) {
                let noRemainingDraftsForCurrentMerge = false;
                const draftsToMerge: Load[] = [];
                let accumulatedWeight = 0;
                const anchorCandidate = draftPool.shift()!;
                const anchorCandidateDestination = anchorCandidate.stops.find(stop => stop.type === StopType.Destination)!;

                if (anchorCandidateDestination.weight! >= weightCapacity) {
                    continue;
                } else {
                    draftsToMerge.push(anchorCandidate);
                    accumulatedWeight += anchorCandidateDestination.weight!;
                }

                while (!noRemainingDraftsForCurrentMerge) {
                    const mergeCandidate = draftPool.find(draft => {
                        const mergeCandidateDestination = draft.stops.find(stop => stop.type === StopType.Destination)!;
                        const anchorPremiseId = organizationLocations.find(loc => loc.id === anchorCandidateDestination.organizationLocationId)?.premiseId;
                        const mergePremiseId = organizationLocations.find(loc => loc.id === mergeCandidateDestination.organizationLocationId)?.premiseId;
                        const hasMatchingPremiseId = mergePremiseId && anchorPremiseId && mergePremiseId === anchorPremiseId;
                            
                        return (mergeCandidateDestination.organizationLocationId === anchorCandidateDestination.organizationLocationId || hasMatchingPremiseId)
                            && mergeCandidateDestination.weight! + accumulatedWeight <= weightCapacity;
                    });
                    if (mergeCandidate) {
                        draftsToMerge.push(mergeCandidate);
                        draftPool.splice(draftPool.indexOf(mergeCandidate), 1);
                        accumulatedWeight += mergeCandidate.stops.find(stop => stop.type === StopType.Destination)!.weight!;
                    } else {
                        noRemainingDraftsForCurrentMerge = true;
                    }
                }

                if (draftsToMerge.length > 1) {
                    const mergedDraft = mergeDrafts(draftsToMerge, throughputs);
                    mergeResults.push(mergedDraft);
                }
            }
        });
        
        setMergeResults(mergeResults);
        setAutoMergeAttempted(true);
    }

    const toggleSelectAllNone = () => {
        if (selectedResultIndexes.length === mergeResults.length) {
            setSelectedResultIndexes([]);
        } else {
            setSelectedResultIndexes(mergeResults.map((_, index) => index));
        }
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        const selectedMergeResults = mergeResults.filter((_, index) => selectedResultIndexes.includes(index));
        submitMergeDrafts({ drafts: selectedMergeResults }, {
            onSuccess: (returnedDrafts) => {
                onSubmit(returnedDrafts.map(d => d.id));
            }
        });
    }

    return (
        <AutoMergeContainer>
            <form onSubmit={handleSubmit} id='merge-drafts-form' name='merge-drafts-form'>
                <ContentContainer>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        {mergeResults.length > 0 && <Button variant='contained' sx={{ width: '11rem' }} onClick={toggleSelectAllNone}>{L.selectAllNone()}</Button>}
                        <Typography variant='h6'>{promptLabel}</Typography>
                        {selectedResultIndexes.length > 0 && <Typography>{`(${L.xSelected(String(selectedResultIndexes.length))})`}</Typography>}
                    </div>
                    <div style={{ display: 'flex' , gap: '1rem' }}>
                        <div style={{ width: '16rem' }}>
                            <AgisticsMultiSelect
                                label={L.weightCapacities()}
                                items={createAgisticsSelectElements(cargoWeightObjects, weightObject => weightObject.value)}
                                selectedIds={selectedWeightCapacities.map(w => w)}
                                setSelectedIds={(ids) => setSelectedWeightCapacities(ids.map(id => id))}
                                disableClearable={false}
                                
                                />
                        </div>
                        <Button color='primary' variant='contained' disabled={selectedWeightCapacities.length === 0} onClick={runAutoMerge}>{L.runAutoMerge()}</Button>
                    </div>
                </ContentContainer>
                <MergeResultsContainer>
                    {mergeResults.length > 0 && mergeResults.map((mergedDraft, index) => (
                        <MergeResultItem key={index}>
                            <Checkbox checked={selectedResultIndexes.includes(index)} onChange={() => {
                                setSelectedResultIndexes(prev => {
                                    if (prev.includes(index)) {
                                        return prev.filter(i => i !== index);
                                    } else {
                                        return [...prev, index];
                                    }
                                });
                            }} />
                            <LoadTableStops load={mergedDraft} />
                        </MergeResultItem>
                    ))}
                </MergeResultsContainer>
                <LoadingButton
                    isLoading={isMergingDraft}
                    icon={<SaveIcon />}
                    type='submit'
                    color='primary'
                    variant='contained'
                    disabled={selectedResultIndexes.length < 1 || isSubmitting}
                    style={{ alignSelf: 'flex-end' }}
                >
                    {L.saveSelected()}
                </LoadingButton>
            </form>
        </AutoMergeContainer>
    )
}

const AutoMergeContainer = styled('div')({
    minWidth: '70rem',
    fontSize: '0.875rem',
    padding: '1rem'
});

const ContentContainer = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
});

const MergeResultsContainer = styled('div')({
    height: '60vh',
    overflowY: 'auto',
    margin: '1rem 0',
    border: `1px solid ${AgisticsColors.lightGray}`
});

const MergeResultItem = styled('div')({
    display: 'flex',
    alignItems: 'center',
    padding: '1rem',
    margin: '1rem'
});